<template>
  <div>
    <TopNav />

    <main class="dashboard">
      <div v-if="loading" class="spinner" aria-label="Lädt"></div>

      <div v-else-if="notFound" class="empty-state">
        Ticket nicht gefunden.
      </div>

      <div v-else-if="loadError" class="error">{{ loadError }}</div>

      <template v-else-if="ticket">
        <div class="dashboard-header">
          <h1 class="dashboard-title">
            {{ ticket.order_number }}
            <span v-if="ticket.is_complaint" class="complaint-badge">Reklamation</span>
          </h1>
          <p class="dashboard-subtitle">{{ ticket.type }}</p>
        </div>

        <div class="process-bar">
          <button
            v-for="p in ticket.available_processes"
            :key="p.key"
            type="button"
            class="btn btn-primary"
            :disabled="closing"
            @click="onProcess(p.key)"
          >{{ p.key === PROCESS_ABSCHLIESSEN && closing ? 'Schließt ab…' : p.label }}</button>
          <button
            type="button"
            class="btn btn-outline-secondary"
            @click="onPrintLabel"
          >Labeldruck</button>
          <button
            v-if="ticket.can_create_stock_device"
            type="button"
            class="btn btn-outline-primary"
            :disabled="creatingStockDevice"
            @click="onCreateStockDevice"
          >{{ creatingStockDevice ? 'Wird eingebucht …' : 'In Lagergerät verwandeln' }}</button>
        </div>

        <div v-if="processError" class="error">{{ processError }}</div>

        <!-- Was die Lagerbuchung zum Austauschgerät getan hat. Erscheint nur, wenn
             tatsächlich etwas zu melden war — bei Tickets ohne Austauschgerät bleibt
             die Liste leer und der Block weg. -->
        <ul v-if="stockNotice.length" class="stock-notice">
          <li v-for="line in stockNotice" :key="line">{{ line }}</li>
        </ul>

        <!-- Zustand der Assona-Meldung zum jüngsten Reparaturbericht.
             Die erfolgreiche Übertragung wird nicht gemeldet - nur was noch aussteht
             oder fehlgeschlagen ist, verlangt eine Reaktion. -->
        <div
          v-if="showTransmissionBanner"
          class="assona-transmission"
          :class="`assona-transmission--${ticket.assona_transmission.state}`"
        >
          <template v-if="ticket.assona_transmission.state === 'pending'">
            {{ transmissionLabel }} steht aus — Übertragung ab
            {{ formatDate(ticket.assona_transmission.due_at) }}.
            <template v-if="ticket.assona_transmission.kind !== 'device_received'">
              Bis dahin ist der Bericht noch korrigierbar.
            </template>
          </template>
          <template v-else>
            <strong>{{ transmissionLabel }} fehlgeschlagen</strong>
            <span v-if="ticket.assona_transmission.transmitted_at">
              ({{ formatDate(ticket.assona_transmission.transmitted_at) }})
            </span>
            <span v-if="ticket.assona_transmission.error">: {{ ticket.assona_transmission.error }}</span>
          </template>
        </div>

        <!-- Block 1: Grunddaten -->
        <section class="ticket-section">
          <dl class="ticket-fields">
            <div class="ticket-field">
              <dt>Erstelldatum</dt>
              <dd>{{ formatDate(ticket.creation_timestamp) }}</dd>
            </div>
            <div class="ticket-field">
              <dt>Status</dt>
              <dd>
                <span class="ticket-status" :style="statusStyle">
                  {{ ticket.status || '—' }}
                  <span v-if="ticket.status_changed_timestamp" class="ticket-status-when">
                    · {{ formatDate(ticket.status_changed_timestamp) }}
                  </span>
                </span>
              </dd>
            </div>
            <div class="ticket-field">
              <dt>Gerät</dt>
              <dd class="ticket-device-dd">
                <span>{{ deviceLabel }}</span>
                <span v-if="ticket.is_legacy_device" class="badge bg-secondary legacy-badge">Altgerät</span>
                <button
                  type="button"
                  class="device-edit-btn"
                  :disabled="deviceSaving"
                  :title="ticket.device || ticket.is_legacy_device ? 'Gerät ändern' : 'Gerät wählen'"
                  :aria-label="ticket.device || ticket.is_legacy_device ? 'Gerät ändern' : 'Gerät wählen'"
                  @click="onEditDevice"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg" width="16" height="16"
                    viewBox="0 0 24 24" fill="none" stroke="currentColor"
                    stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                    aria-hidden="true"
                  >
                    <path d="M12 20h9" />
                    <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5z" />
                  </svg>
                </button>
                <span v-if="deviceError" class="error device-error">{{ deviceError }}</span>
                <span v-if="deviceVariantLabel" class="ticket-device-variant">{{ deviceVariantLabel }}</span>
              </dd>
            </div>
            <div class="ticket-field">
              <dt>IMEI / SN</dt>
              <dd class="ticket-mono">{{ ticket.imei_sn || '—' }}</dd>
            </div>
            <!-- Wird beim Abschluss automatisch ausgefuehrt: Etikett + Versandzeile.
                 Geaendert wird er ueber denselben Stift wie das Geraet. -->
            <div v-if="ticket.planned_shipment" class="ticket-field">
              <dt>{{ ticket.is_closed ? 'Versand an' : 'Versand geplant' }}</dt>
              <dd>
                {{ ticket.planned_shipment.servicepartner.description }}
                <span class="ticket-muted">
                  · {{ ticket.planned_shipment.error_pattern.description }}
                  <template v-if="ticket.planned_shipment.price_label">
                    · {{ ticket.planned_shipment.price_label }}{{ ticket.planned_shipment.is_manual_price ? ' (händisch)' : '' }}
                  </template>
                </span>
              </dd>
            </div>
            <div v-if="ticket.stock_device" class="ticket-field">
              <dt>Lagergerät</dt>
              <dd>
                <router-link :to="{ name: 'stockdevices', query: { q: ticket.stock_device.stock_number } }">{{ ticket.stock_device.stock_number }}</router-link>
              </dd>
            </div>
            <div v-if="ticket.is_closed" class="ticket-field">
              <dt>Abgeschlossen</dt>
              <dd>{{ formatDate(ticket.closed_at) }}<span v-if="ticket.closed_by"> · {{ ticket.closed_by }}</span></dd>
            </div>
            <!-- Nur Anzeige: verrechnet wird in Zeus („Assona Verrechnung"). -->
            <div class="ticket-field">
              <dt>Verrechnung</dt>
              <dd>
                <template v-if="ticket.billing.is_billed">
                  {{ ticket.billing.invoice_number || '—' }}
                  <span v-if="ticket.billing.billed_at" class="ticket-muted">
                    · {{ formatDate(ticket.billing.billed_at) }}
                  </span>
                </template>
                <span v-else class="ticket-muted">nicht verrechnet</span>
              </dd>
            </div>
          </dl>
        </section>

        <!-- Block 2: Assona24H-Daten -->
        <section v-if="isAssona" class="ticket-section">
          <h2 class="ticket-section-title">Assona24H</h2>
          <dl class="ticket-fields">
            <div class="ticket-field">
              <dt>Schadensnummer</dt>
              <dd class="ticket-mono">{{ ticket.assona_damage_nr || '—' }}</dd>
            </div>
            <div class="ticket-field">
              <dt>Vertragsnummer</dt>
              <dd class="ticket-mono">{{ ticket.assona_contract_nr || '—' }}</dd>
            </div>
            <div class="ticket-field">
              <dt>Erwartetes Gerät</dt>
              <dd>
                {{ ticket.original_device_description || '—' }}
                <!-- Darunter das Gerät, das die IMEI-Prüfung ergeben hat (nur wenn vorhanden). -->
                <div v-if="ticket.imei_check_device" class="imei-check-device">
                  {{ ticket.imei_check_device }}
                </div>
              </dd>
            </div>
            <div class="ticket-field">
              <dt>Bezeichnung Kundeneingabe</dt>
              <dd>{{ ticket.customer_input_description || '—' }}</dd>
            </div>
            <div class="ticket-field">
              <dt>Task-ID</dt>
              <dd class="ticket-mono">{{ ticket.task_id ?? '—' }}</dd>
            </div>
            <!-- Alle Tasks zur Schadensnummer. Genau einer ist der Normalfall (blau);
                 mehrere deuten auf einen Sonderfall hin, meist eine Reklamation —
                 deshalb dort gelb und blinkend. -->
            <div class="ticket-field">
              <dt>Tasks</dt>
              <dd class="ticket-mono">
                <span
                  v-if="ticket.assona_task_ids.length"
                  class="task-list"
                  :class="hasMultipleTasks ? 'task-list-many' : 'task-list-single'"
                  :title="hasMultipleTasks
                    ? 'Mehrere Tasks zu dieser Schadensnummer — moeglicher Sonderfall (z. B. Reklamation).'
                    : undefined"
                >{{ ticket.assona_task_ids.join(', ') }}</span>
                <span v-else>—</span>
              </dd>
            </div>
            <!-- Reklamation: Bezug zum Original-Austausch (Task + R2-Ticket). -->
            <div v-if="ticket.is_complaint" class="ticket-field">
              <dt>Reklamation zu</dt>
              <dd class="ticket-mono">
                <template v-if="ticket.complaint_origin">
                  Task {{ ticket.complaint_origin.task_id }}
                  <template v-for="id in ticket.complaint_origin.ticket_ids" :key="id">
                    · <router-link :to="{ name: 'ticket-detail', params: { id } }">R2-{{ id }}</router-link>
                  </template>
                </template>
                <span v-else class="text-muted">Originalfall unbekannt</span>
              </dd>
            </div>
            <!-- Original: Reklamationen dazu, auch solche ohne R2-Fall. -->
            <div v-if="ticket.complaints.length" class="ticket-field">
              <dt>Reklamationen</dt>
              <dd class="ticket-mono">
                <div v-for="c in ticket.complaints" :key="c.task_id">
                  Task {{ c.task_id }}
                  <template v-for="id in c.ticket_ids" :key="id">
                    · <router-link :to="{ name: 'ticket-detail', params: { id } }">R2-{{ id }}</router-link>
                  </template>
                  <span v-if="!c.ticket_ids.length" class="text-muted">· noch kein R2-Fall</span>
                </div>
              </dd>
            </div>
            <!-- „Anzeigen" liest Gespeichertes, „Abfragen" führt zur kostenpflichtigen
                 Abfrage — die Beschriftung sagt vorher, was der Klick auslöst. -->
            <div v-if="ticket.imei_sn" class="ticket-field">
              <dt>IMEI Details</dt>
              <dd>
                <button
                  type="button"
                  class="btn btn-sm btn-outline-secondary"
                  @click="onShowImeiInformation"
                >{{ ticket.has_imei_information ? 'Anzeigen' : 'Abfragen' }}</button>
              </dd>
            </div>
          </dl>
        </section>

        <!-- Block 3: Befund -->
        <section class="ticket-section">
          <h2 class="ticket-section-title">Befund</h2>

          <div class="ticket-field ticket-field-block">
            <dt>Fehlerbeschreibung</dt>
            <dd class="ticket-description">{{ ticket.damage_description || '—' }}</dd>
          </div>

          <dl class="ticket-fields ticket-fields-spaced">
            <div class="ticket-field">
              <dt>FMIP-Check</dt>
              <dd class="fmip-dd">
                <span class="fmip" :class="`fmip-${ticket.fmip_status}`">{{ fmipLabel }}</span>
                <span v-if="ticket.fmip_checked_at" class="fmip-checked">
                  zuletzt geprüft: {{ formatDate(ticket.fmip_checked_at) }}
                </span>
                <button
                  type="button"
                  class="btn btn-sm btn-outline-secondary fmip-refresh-btn"
                  :disabled="fmipRefreshing"
                  @click="onRefreshFmip"
                >{{ fmipRefreshing ? 'Frage ab…' : 'Erneut abfragen' }}</button>
                <span v-if="fmipRefreshError" class="error fmip-refresh-error">{{ fmipRefreshError }}</span>
              </dd>
            </div>
            <div v-if="ticket.imei_verified" class="ticket-field">
              <dt>IMEI verifiziert</dt>
              <dd>Ja</dd>
            </div>
            <div v-else-if="ticket.imei_not_verified_reason" class="ticket-field">
              <dt>IMEI nicht verifiziert</dt>
              <dd>{{ ticket.imei_not_verified_reason }}</dd>
            </div>
            <div v-if="ticket.battery_test_capacity !== null" class="ticket-field">
              <dt>Akku-Kapazität</dt>
              <dd class="ticket-mono">{{ ticket.battery_test_capacity }} %</dd>
            </div>
            <div v-else-if="ticket.battery_test_reject_reasons" class="ticket-field">
              <dt>Akkutest</dt>
              <dd>nicht möglich: {{ ticket.battery_test_reject_reasons }}</dd>
            </div>
          </dl>
        </section>

        <!-- Block 4: Dateien -->
        <TicketFilesPanel :ticket-id="ticket.id" :files="ticket.files" @changed="reloadTicket" />

        <!-- Block 5: Kostenvoranschläge -->
        <CostEstimatePanel ref="costEstimatePanel" :ticket="ticket" @changed="reloadTicket" />

        <!-- Block 6: Reparaturberichte -->
        <RepReportPanel ref="repReportPanel" :ticket="ticket" @changed="reloadTicket" />

        <section class="ticket-section ticket-history">
          <h2 class="history-title">Verlauf</h2>
          <ol v-if="ticket.history.length" class="history-list">
            <template v-for="row in displayedHistory" :key="row.type === 'more' ? 'more' : row.entry.id">
              <li v-if="row.type === 'entry'" class="history-item">
                <span class="history-date">{{ formatDate(row.entry.creation_timestamp) }}</span>
                <span class="history-user">{{ row.entry.user_name }}</span>
                <span v-if="row.entry.note" class="history-note">{{ row.entry.note }}</span>
                <pre v-else-if="row.entry.json_data" class="history-json">{{ formatJson(row.entry.json_data) }}</pre>
              </li>
              <li v-else class="history-more">
                <button type="button" class="btn btn-link history-more-btn" @click="showAllHistory = true">
                  Weitere {{ row.hidden }} {{ row.hidden === 1 ? 'Eintrag' : 'Einträge' }}…
                </button>
              </li>
            </template>
          </ol>
          <p v-else class="history-empty">Noch keine Verlaufseinträge.</p>
          <button
            v-if="showAllHistory && historyHiddenCount > 0"
            type="button"
            class="btn btn-link history-more-btn"
            @click="showAllHistory = false"
          >Weniger anzeigen</button>
        </section>
      </template>
    </main>

    <EingelangtProcessModal ref="eingelangtModal" />
    <SwapDocumentCreateModal ref="swapDocumentModal" />
    <LabelPrintModal ref="labelModal" />
    <ImeiInformationModal ref="imeiInformationModal" />
    <DeviceSelectModal ref="deviceModal" />
    <ConfirmModal
      ref="confirmModal"
      title="Ticket abschließen"
      confirm-label="Abschließen"
    />
    <AlertModal ref="alertModal" :title="alertTitle" />
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, useTemplateRef } from 'vue';
import TopNav from '../components/TopNav.vue';
import EingelangtProcessModal from '../components/EingelangtProcessModal.vue';
import SwapDocumentCreateModal from '../components/SwapDocumentCreateModal.vue';
import LabelPrintModal from '../components/LabelPrintModal.vue';
import ImeiInformationModal from '../components/ImeiInformationModal.vue';
import DeviceSelectModal from '../components/DeviceSelectModal.vue';
import ConfirmModal from '../components/ConfirmModal.vue';
import AlertModal from '../components/AlertModal.vue';
import TicketFilesPanel from '../components/TicketFilesPanel.vue';
import CostEstimatePanel from '../components/CostEstimatePanel.vue';
import RepReportPanel from '../components/RepReportPanel.vue';
import {
  get, refreshFmip, setTicketDevice, closeTicket, createStockDevice, ApiError,
  PROCESS_ABSCHLIESSEN, PROCESS_EINGELANGT, PROCESS_KOSTENVORANSCHLAG, PROCESS_REPARATURBERICHT,
  PROCESS_KV_REPBERICHT,
  STEP_BOOK_OUT, STEP_CLOSE_DELIVERY,
  type PlannedShipmentResult, type SwapStockBookingResult, type TicketDetail, type TicketHistoryEntry,
} from '../services/tickets';

const props = defineProps<{ id: number }>();

const ticket    = ref<TicketDetail | null>(null);
const loading   = ref(true);
const loadError = ref<string | null>(null);
const notFound  = ref(false);

const eingelangtModal = useTemplateRef<InstanceType<typeof EingelangtProcessModal>>('eingelangtModal');
const swapDocumentModal = useTemplateRef<InstanceType<typeof SwapDocumentCreateModal>>('swapDocumentModal');
const labelModal      = useTemplateRef<InstanceType<typeof LabelPrintModal>>('labelModal');
const imeiInformationModal = useTemplateRef<InstanceType<typeof ImeiInformationModal>>('imeiInformationModal');
const deviceModal     = useTemplateRef<InstanceType<typeof DeviceSelectModal>>('deviceModal');
const confirmModal    = useTemplateRef<InstanceType<typeof ConfirmModal>>('confirmModal');
const alertModal      = useTemplateRef<InstanceType<typeof AlertModal>>('alertModal');

const costEstimatePanel = useTemplateRef<InstanceType<typeof CostEstimatePanel>>('costEstimatePanel');
const repReportPanel    = useTemplateRef<InstanceType<typeof RepReportPanel>>('repReportPanel');

/** Gerät in der ersten Zeile — beim Altgerät die händische Bezeichnung. */
const deviceLabel = computed<string>(() => {
  const t = ticket.value;
  if (!t) return '—';
  if (t.device) return t.device.description;
  return t.is_legacy_device ? (t.alternative_device_description ?? '—') : '—';
});

/** Zweite Zeile unter dem Gerät: Farbe · Speicher · Zustand, soweit gewählt. */
const deviceVariantLabel = computed<string>(() => {
  const t = ticket.value;
  if (!t) return '';
  const parts: string[] = [];
  if (t.device_color && t.device_memory_size) {
    parts.push(t.device_color.description, t.device_memory_size_label ?? '');
  }
  if (t.device_condition) {
    parts.push(t.device_condition.description);
  }
  return parts.filter(p => p !== '').join(' · ');
});

const closing              = ref(false);
const creatingStockDevice  = ref(false);
const alertTitle           = ref('Hinweis');
const processError         = ref<string | null>(null);

/**
 * Was die Lagerbuchung zuletzt getan hat, in ganzen Sätzen.
 *
 * Erledigtes und schon-Erledigtes stehen gleichberechtigt nebeneinander: beim
 * zweiten Dokumentensatz zum selben Ticket ist „war bereits ausgebucht" die
 * richtige Auskunft und kein Fehler — genau das soll der Bearbeiter sehen,
 * statt sich zu fragen, ob er gerade doppelt gebucht hat.
 */
const stockNotice = ref<string[]>([]);

const STOCK_STEP_ALREADY: Record<string, string> = {
  [STEP_CLOSE_DELIVERY]: 'Lieferschein war bereits abgeschlossen.',
  [STEP_BOOK_OUT]:       'Austauschgerät war bereits ausgebucht.',
};

function stockBookingLines(buchung: SwapStockBookingResult, t: TicketDetail): string[] {
  const slip  = t.swap_stock.delivery_slip_number;
  const imei  = t.swap_stock.imei;
  const lines: string[] = [];
  for (const step of buchung.performed) {
    if (step === STEP_CLOSE_DELIVERY) {
      lines.push(slip ? `Lieferschein ${slip} abgeschlossen.` : 'Lieferschein abgeschlossen.');
    } else if (step === STEP_BOOK_OUT) {
      lines.push(imei ? `Austauschgerät ausgebucht (IMEI ${imei}).` : 'Austauschgerät ausgebucht.');
    }
  }
  for (const step of buchung.skipped) {
    if (STOCK_STEP_ALREADY[step]) lines.push(STOCK_STEP_ALREADY[step]);
  }
  return lines;
}

const deviceSaving = ref(false);
const deviceError  = ref<string | null>(null);

function onPrintLabel(): void {
  if (!ticket.value) return;
  void labelModal.value?.print(ticket.value.id);
}

async function onShowImeiInformation(): Promise<void> {
  if (!ticket.value) return;
  const hasInformation = await imeiInformationModal.value?.open(ticket.value.id);
  // Nach einer erfolgreichen Abfrage liegt die Auskunft vor - der Knopf heißt ab
  // jetzt „Anzeigen", ohne dass das Ticket neu geladen werden muss.
  if (hasInformation && ticket.value) {
    ticket.value.has_imei_information = true;
  }
}

async function onEditDevice(): Promise<void> {
  if (!ticket.value || deviceSaving.value) return;
  const chosen = await deviceModal.value?.open({
    device_id:    ticket.value.device?.id ?? null,
    color_id:     ticket.value.device_color?.id ?? null,
    memory_size:  ticket.value.device_memory_size,
    condition_id: ticket.value.device_condition?.id ?? null,
    planned_servicepartner_location_id: ticket.value.planned_shipment?.servicepartner.id ?? null,
    planned_error_pattern_id:           ticket.value.planned_shipment?.error_pattern.id ?? null,
    is_closed:    ticket.value.is_closed,
    alternative_device_description: ticket.value.is_legacy_device ? ticket.value.alternative_device_description : null,
    planned_price_net_eur: ticket.value.planned_shipment?.is_manual_price
      ? ticket.value.planned_shipment.price_net_eur : null,
  });
  if (!chosen) return;
  deviceSaving.value = true;
  deviceError.value  = null;
  try {
    ticket.value = await setTicketDevice(
      ticket.value.id,
      chosen.device?.id ?? null,
      chosen.color_id,
      chosen.memory_size,
      chosen.condition_id,
      chosen.planned_servicepartner_location_id,
      chosen.planned_error_pattern_id,
      chosen.alternative_device_description,
      chosen.planned_price_net_eur,
    );
  } catch (e) {
    deviceError.value = deviceSaveMessage(e);
  } finally {
    deviceSaving.value = false;
  }
}

/** Fehlercodes der Gerätewahl in Klartext — der Rest kommt als Meldung durch. */
function deviceSaveMessage(e: unknown): string {
  if (e instanceof ApiError) {
    if (e.message === 'planned_shipment_not_available') {
      return 'Zu diesem Gerät gibt es für den gewählten Servicepartner und das Fehlerbild keine Rezeptur.';
    }
    if (e.message === 'planned_shipment_ticket_closed') {
      return 'Das Ticket ist abgeschlossen — der geplante Versand lässt sich nicht mehr ändern.';
    }
    if (e.message === 'planned_shipment_incomplete') {
      return 'Bitte zum Servicepartner auch ein Fehlerbild wählen.';
    }
    if (e.message === 'alternative_description_missing') {
      return 'Bitte eine Bezeichnung für das Altgerät eingeben.';
    }
    if (e.message === 'alternative_description_too_long') {
      return 'Die Bezeichnung des Altgeräts ist zu lang.';
    }
    if (e.message === 'manual_price_invalid') {
      return 'Bitte einen Netto-Preis zwischen 0,01 € und 9.999,99 € angeben.';
    }
    if (e.message === 'manual_price_not_allowed') {
      return 'Ein händischer Preis ist nur bei Altgeräten ohne Masterartikel möglich.';
    }
  }
  return e instanceof Error ? e.message : 'Gerät konnte nicht gesetzt werden.';
}

/**
 * Welche Prozesse angeboten werden, entscheidet ausschließlich der Server
 * (`available_processes`). Hier steht nur die Zuordnung Schlüssel → Aktion.
 */
function onProcess(key: string): void {
  processError.value = null;
  stockNotice.value  = [];
  switch (key) {
    case PROCESS_EINGELANGT:        void openEingelangt(); break;
    case PROCESS_KOSTENVORANSCHLAG: void costEstimatePanel.value?.openCreate(); break;
    case PROCESS_REPARATURBERICHT:  void repReportPanel.value?.openCreate(); break;
    case PROCESS_KV_REPBERICHT:     void openSwapDocuments(); break;
    case PROCESS_ABSCHLIESSEN:      void onClose(); break;
  }
}

async function onClose(): Promise<void> {
  if (!ticket.value || closing.value) return;

  // Fehlende Gerätedaten sagen wir hier, nicht schon beim Öffnen des Tickets —
  // sonst steht die Meldung ab der ersten Sekunde da, ohne dass jemand etwas
  // falsch gemacht hätte.
  const missing = missingDeviceMessage(ticket.value);
  if (missing !== null) {
    alertTitle.value = 'Abschließen nicht möglich';
    await alertModal.value?.open(missing);
    return;
  }

  const confirmed = await confirmModal.value?.open(
    `Ticket ${ticket.value.order_number} wirklich abschließen? Danach sind keine weiteren `
    + 'Kostenvoranschläge und Reparaturberichte mehr möglich.'
  );
  if (!confirmed) return;

  closing.value      = true;
  processError.value = null;
  try {
    // Antwort ist das frische Ticket — kein Neuladen nötig.
    const closed = await closeTicket(ticket.value.id);
    ticket.value = closed.ticket;
    applyPlannedShipmentResult(closed.planned_shipment);
  } catch (e) {
    // Der Server prüft dieselben Pflichtangaben noch einmal (z.B. wenn ein
    // anderer Benutzer das Gerät zwischenzeitlich geleert hat) — dann kommt die
    // Meldung genauso als Modal, nicht als Zeile irgendwo auf der Seite.
    const message = closeErrorMessage(e);
    if (e instanceof ApiError && deviceGateMessage(e.message) !== null) {
      alertTitle.value = 'Abschließen nicht möglich';
      await alertModal.value?.open(message);
    } else {
      processError.value = message;
    }
  } finally {
    closing.value = false;
  }
}

/**
 * Der vorgemerkte Versand läuft mit dem Lagergerät mit. Er darf den Abschluss nicht
 * kippen: Erfolg steht als Zeile in der Prozessleiste, ein Fehlschlag als Fehler
 * daneben — nachgeholt wird er im Gerätelager. Dauerhaft steht beides im Verlauf.
 */
function applyPlannedShipmentResult(result: PlannedShipmentResult): void {
  if (!result.attempted) return;
  applyInstantClosingResult(result);
  if (result.printed) {
    stockNotice.value.push('Versandetikett gedruckt, Versand an den Servicepartner angelegt.');
    return;
  }
  // Angelegt, aber nicht gedruckt: kein Fehler im eigentlichen Sinn — nachzuholen ist
  // nur das Papier, und die Servermeldung sagt schon, wo es liegt.
  if (result.created) {
    stockNotice.value.push('Versand an den Servicepartner angelegt, aber nicht gedruckt: '
      + (result.message ?? 'unbekannter Fehler'));
    return;
  }
  processError.value = `Der geplante Versand wurde nicht angelegt: ${result.message ?? 'unbekannter Fehler'} `
    + 'Er kann im Gerätelager über „Versand an Servicepartner" nachgeholt werden.';
}

/**
 * Interner Servicepartner: Lieferschein und Übernahme laufen gleich mit. Gelingt das,
 * ist es eine Zeile in der Prozessleiste; sonst ein Hinweis, wo es weitergeht.
 */
function applyInstantClosingResult(result: PlannedShipmentResult): void {
  const instant = result.instant_closing;
  if (!instant?.attempted) return;
  if (instant.completed) {
    stockNotice.value.push(`Sofortiger LS-Abschluss: Lieferschein ${instant.slip_number ?? ''} angelegt und beim Servicepartner übernommen.`);
    return;
  }
  stockNotice.value.push('Sofortiger LS-Abschluss nicht vollständig: ' + (instant.message ?? 'unbekannter Fehler'));
}

/** Was für den Abschluss am Gerät fehlt — null, wenn alles da ist. */
function missingDeviceMessage(t: TicketDetail): string | null {
  if (!t.device && !t.is_legacy_device) {
    return 'Bitte zuerst das eingegangene Gerät wählen (Stift in der Zeile „Gerät").';
  }
  // Altgerät: die Bezeichnung ersetzt Master, Farbe und Speicher.
  if (!t.is_legacy_device && (!t.device_color || !t.device_memory_size)) {
    return 'Bitte zuerst Farbe und Speichergröße des Geräts wählen (Stift in der Zeile „Gerät").';
  }
  if (!t.device_condition) {
    return 'Bitte zuerst den Zustand des Geräts wählen (Stift in der Zeile „Gerät").';
  }
  return null;
}

/**
 * Bestandstickets, die vor dem Gerätelager abgeschlossen wurden: Master, Farbe,
 * Speicher und Zustand werden nachgetragen (Stift beim Gerät), danach entsteht
 * hierüber das Lagergerät. Fehlt noch etwas, sagt es das Modal.
 */
async function onCreateStockDevice(): Promise<void> {
  if (!ticket.value || creatingStockDevice.value) return;
  creatingStockDevice.value = true;
  processError.value        = null;
  stockNotice.value         = [];
  try {
    const booked = await createStockDevice(ticket.value.id);
    ticket.value = booked.ticket;
    applyPlannedShipmentResult(booked.planned_shipment);
  } catch (e) {
    alertTitle.value = 'In Lagergerät verwandeln';
    await alertModal.value?.open(stockDeviceErrorMessage(e));
  } finally {
    creatingStockDevice.value = false;
  }
}

/** Fehlercodes der nachträglichen Einbuchung in Klartext. */
function stockDeviceErrorMessage(e: unknown): string {
  if (e instanceof ApiError) {
    if (e.message === 'stock_device_exists') {
      return 'Zu diesem Ticket gibt es bereits ein Lagergerät.';
    }
    if (e.message === 'ticket_not_closed') {
      return 'Nur abgeschlossene Tickets lassen sich in ein Lagergerät verwandeln.';
    }
    // Gerät, Variante und Zustand melden dieselben Codes wie beim Abschluss.
    return deviceGateMessage(e.message) ?? `Einbuchen fehlgeschlagen: ${e.message}`;
  }
  return e instanceof Error ? e.message : 'Das Lagergerät konnte nicht angelegt werden.';
}

/**
 * Die drei Pflichtangaben am Gerät, die Abschluss und Einbuchung gleichermaßen
 * verlangen — ein Text je Code, egal welcher der beiden Wege darauf läuft.
 */
function deviceGateMessage(reason: string): string | null {
  switch (reason) {
    case 'device_missing':
      return 'Bitte zuerst das eingegangene Gerät wählen (Stift in der Zeile „Gerät").';
    case 'device_variant_missing':
      return 'Bitte zuerst Farbe und Speichergröße des Geräts wählen (Stift in der Zeile „Gerät").';
    case 'device_condition_missing':
      return 'Bitte zuerst den Zustand des Geräts wählen (Stift in der Zeile „Gerät").';
    default:
      return null;
  }
}

/** Übersetzt die Fehlercodes des Abschlusses in Sätze, die sagen, was zu tun ist. */
function closeErrorMessage(e: unknown): string {
  if (e instanceof ApiError) {
    const gate = deviceGateMessage(e.message);
    if (gate !== null) {
      return gate;
    }
    return `Abschließen fehlgeschlagen: ${e.message}`;
  }
  return e instanceof Error ? `Abschließen fehlgeschlagen: ${e.message}` : 'Abschließen fehlgeschlagen.';
}

/**
 * Assona24H: KV und Reparaturbericht in einem Schritt. Die Antwort ist das frische
 * Ticket (Status und beide Dokumente schon drin) — kein Neuladen nötig.
 */
async function openSwapDocuments(): Promise<void> {
  if (!ticket.value) return;
  const updated = await swapDocumentModal.value?.open(ticket.value);
  if (!updated) return;

  ticket.value = updated;

  // Die Dokumente gelten auch, wenn die Lagerbuchung schiefging — wie beim
  // Wareneingang. Was tatsächlich gebucht wurde, steht in der Prozessleiste,
  // dauerhaft im Ticketverlauf.
  const buchung = swapDocumentModal.value?.lastStockBooking();
  stockNotice.value  = buchung ? stockBookingLines(buchung, updated) : [];
  processError.value = buchung?.failed
    ? (buchung.error ?? 'Die Lagerbuchung ist fehlgeschlagen.')
    : null;

  // Die beiden Panels laden nur bei Wechsel der Ticket-ID nach — die bleibt hier
  // gleich, die Dokumente entstehen aber ausserhalb der Panels. Also anstossen.
  await Promise.all([
    costEstimatePanel.value?.reload(),
    repReportPanel.value?.reload(),
  ]);
}

async function openEingelangt(): Promise<void> {
  if (!ticket.value) return;
  const updated = await eingelangtModal.value?.open(ticket.value);
  if (updated) {
    ticket.value = updated;
  }
}

const fmipRefreshing  = ref(false);
const fmipRefreshError = ref<string | null>(null);

async function onRefreshFmip() {
  if (!ticket.value || fmipRefreshing.value) return;
  fmipRefreshing.value  = true;
  fmipRefreshError.value = null;
  try {
    const result = await refreshFmip(props.id);
    ticket.value.fmip_status       = result.fmip_status;
    ticket.value.fmip_checked_at   = result.fmip_checked_at;
    ticket.value.imei_check_device = result.imei_check_device;
  } catch (e) {
    fmipRefreshError.value = e instanceof Error
      ? `Abfrage fehlgeschlagen: ${e.message}`
      : 'Abfrage fehlgeschlagen.';
  } finally {
    fmipRefreshing.value = false;
  }
}

const fmipLabel = computed(() => {
  switch (ticket.value?.fmip_status) {
    case 'active':   return 'aktiv';
    case 'inactive': return 'nicht aktiv';
    default:         return 'unbekannt';
  }
});

const isAssona = computed(() => ticket.value?.type === 'Assona24H');

// Benennt die Meldung, um die es geht. Vorher stand hier pauschal
// „Reparaturbericht" — auch beim Geraeteeingang, der schon im Wareneingang
// entsteht und mit einem Bericht nichts zu tun hat.
const TRANSMISSION_LABELS: Record<string, string> = {
  device_received: 'Meldung des Geräteeingangs an Assona',
  rep_report:      'Assona-Meldung des Reparaturberichts',
  total_loss:      'Assona-Meldung des Totalschadens',
};
const transmissionLabel = computed<string>(() => {
  const kind = ticket.value?.assona_transmission.kind;
  return (kind && TRANSMISSION_LABELS[kind]) ?? 'Assona-Meldung';
});

/**
 * Der Hinweis erscheint nur, wenn er etwas wert ist.
 *
 * Beim Geraeteeingang steht die ausstehende Meldung ohne Handlungsbedarf da —
 * sie geht raus, und es gibt nichts daran zu korrigieren. Nur ein FEHLSCHLAG ist
 * auch dort eine Nachricht, weil dann jemand hinsehen muss.
 * Bei Bericht und Totalschaden bleibt der Hinweis, weil das Karenzfenster die
 * Gelegenheit zur Korrektur ist.
 */
const showTransmissionBanner = computed<boolean>(() => {
  const t = ticket.value?.assona_transmission;
  if (!t) return false;
  if (t.state === 'failed') return true;
  return t.state === 'pending' && t.kind !== 'device_received';
});

// Mehr als ein Task zur Schadensnummer = moeglicher Sonderfall (Reklamation/Diebstahl).
const hasMultipleTasks = computed(() => (ticket.value?.assona_task_ids.length ?? 0) > 1);

// Status-Badge in seiner DB-Farbe (Soft-Tint + gesättigte Schrift), Fallback = Akzent-Orange.
const statusStyle = computed<Record<string, string> | undefined>(() => {
  const t = ticket.value;
  if (!t || (!t.status_bg && !t.status_fg)) return undefined;
  return {
    background: t.status_bg || 'var(--accent-soft, rgba(247, 166, 0, 0.10))',
    color:      t.status_fg || 'var(--accent-hover, #e09600)',
  };
});

// Verlauf: erste 2 + letzte 3 anzeigen, Mittelteil hinter „Weitere N Einträge…".
type HistoryRow =
  | { type: 'entry'; entry: TicketHistoryEntry }
  | { type: 'more'; hidden: number };

const HISTORY_HEAD = 2;
const HISTORY_TAIL = 3;
const showAllHistory = ref(false);

const historyHiddenCount = computed(() =>
  Math.max(0, (ticket.value?.history.length ?? 0) - HISTORY_HEAD - HISTORY_TAIL),
);

const displayedHistory = computed<HistoryRow[]>(() => {
  const h = ticket.value?.history ?? [];
  if (showAllHistory.value || h.length <= HISTORY_HEAD + HISTORY_TAIL) {
    return h.map((entry): HistoryRow => ({ type: 'entry', entry }));
  }
  return [
    ...h.slice(0, HISTORY_HEAD).map(entry => ({ type: 'entry', entry }) as HistoryRow),
    { type: 'more', hidden: h.length - HISTORY_HEAD - HISTORY_TAIL },
    ...h.slice(h.length - HISTORY_TAIL).map(entry => ({ type: 'entry', entry }) as HistoryRow),
  ];
});

/** Ticket neu laden (nach Datei-Upload/-Löschung im TicketFilesPanel). */
async function reloadTicket(): Promise<void> {
  try {
    ticket.value = await get(props.id);
  } catch {
    /* Fehler werden im Panel angezeigt; Bestand bleibt erhalten. */
  }
}

onMounted(async () => {
  try {
    ticket.value = await get(props.id);
  } catch (e) {
    if (e instanceof ApiError && e.code === 'NOT_FOUND') {
      notFound.value = true;
    } else {
      loadError.value = e instanceof Error ? e.message : 'Unbekannter Fehler.';
    }
  } finally {
    loading.value = false;
  }
});

function formatDate(value: string | null): string {
  if (!value) return '—';
  const d = new Date(value.replace(' ', 'T'));
  if (Number.isNaN(d.getTime())) return value;
  return new Intl.DateTimeFormat('de-AT', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  }).format(d);
}

function formatJson(value: Record<string, unknown>): string {
  return JSON.stringify(value, null, 2);
}
</script>

<style scoped>
.dashboard {
  max-width: 1200px;
}

.ticket-muted { color: var(--muted, #6c757d); }

/* Zustand der Assona-Meldung — dezent, ausser bei Fehlschlag. */
.assona-transmission {
  margin: 0 0 12px;
  padding: 8px 12px;
  border-radius: 8px;
  font-size: 13px;
  border: 1px solid transparent;
}
.assona-transmission--pending { background: #fff8e6; border-color: #f2dfae; color: #7a5b00; }
.assona-transmission--failed  { background: #fdecee; border-color: #f3c6cc; color: #b02a37; }

/* Ergebnis der Lagerbuchung — Auskunft, keine Warnung: dezent gruen statt Alarm. */
.stock-notice {
  margin: 0 0 12px;
  padding: 8px 12px 8px 28px;
  border-radius: 8px;
  font-size: 13px;
  border: 1px solid #cfe6d4;
  background: #f1f8f3;
  color: #1e6b39;
  list-style: disc;
}
.stock-notice li + li {
  margin-top: 2px;
}

/* Reklamation: Folgefall eines früheren Austauschs, kostet Assona nichts. */
.complaint-badge {
  display: inline-block;
  margin-left: 10px;
  padding: 2px 10px;
  border-radius: 9999px;
  background: #fff3cd;
  color: #8a5a00;
  border: 1px solid #f0c36d;
  font-size: 0.5em;
  font-weight: 600;
  vertical-align: middle;
}

.task-list {
  display: inline-block;
  padding: 1px 6px;
  border-radius: var(--radius-sm, 8px);
}
.task-list-single {
  color: #0a58ca;
  background: rgba(10, 88, 202, 0.10);
}
/* Warnung: hier lohnt ein Blick in die anderen Tasks, bevor gearbeitet wird.
   Das Blinken wechselt zwischen zwei SICHTBAREN Gelbtoenen - nicht gegen
   transparent. Sonst ist die Warnung die halbe Zeit keine, und wer im falschen
   Moment hinsieht, haelt das Feld fuer unauffaellig. */
.task-list-many {
  color: #7a5200;
  background: rgba(247, 166, 0, 0.22);
  font-weight: 600;
  animation: task-blink 1.1s steps(1, end) infinite;
}
@keyframes task-blink {
  50% { background: rgba(247, 166, 0, 0.65); }
}
/* Wer reduzierte Bewegung eingestellt hat, bekommt die Farbe - aber kein Blinken. */
@media (prefers-reduced-motion: reduce) {
  .task-list-many { animation: none; }
}

.process-bar {
  margin-top: 20px;
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

/* .ticket-section / .ticket-section-title jetzt global in apple.css */

.ticket-fields {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 16px 24px;
  margin: 0;
}

.ticket-fields-spaced {
  margin-top: 20px;
}

.ticket-field dt {
  color: #6c757d;
  font-size: 13px;
  font-weight: 600;
  margin-bottom: 2px;
}

.ticket-field dd {
  margin: 0;
  font-size: 15px;
}

.ticket-mono {
  font-variant-numeric: tabular-nums;
  font-weight: 600;
}

.ticket-device-dd {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px 8px;
}

.device-edit-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  padding: 0;
  border: none;
  border-radius: 6px;
  background: transparent;
  color: var(--muted, #6c757d);
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease;
}

.device-edit-btn:hover:not(:disabled) {
  background: var(--accent-soft, rgba(247, 166, 0, 0.12));
  color: var(--accent-hover, #e09600);
}

.device-edit-btn:disabled {
  opacity: 0.5;
  cursor: default;
}

/* Eigene Zeile unter Gerät + Stift — flex-basis bricht die Zeile im flex-Container um. */
.legacy-badge {
  margin-left: 0.4rem;
  font-weight: 500;
}

.ticket-device-variant {
  flex: 0 0 100%;
  color: var(--bs-secondary-color, #6c757d);
}

.device-error {
  flex-basis: 100%;
}

.ticket-status {
  display: inline-flex;
  align-items: baseline;
  gap: 6px;
  padding: 4px 12px;
  border-radius: 9999px;
  background: var(--accent-soft, rgba(247, 166, 0, 0.10));
  color: var(--accent-hover, #e09600);
  font-size: 13px;
  font-weight: 600;
  white-space: nowrap;
}

/* Zeitpunkt des letzten Statuswechsels — im selben Badge, aber zurückgenommen. */
.ticket-status-when {
  font-weight: 500;
  opacity: 0.75;
  font-variant-numeric: tabular-nums;
}

.fmip {
  display: inline-flex;
  align-items: center;
  padding: 4px 12px;
  border-radius: 9999px;
  font-size: 13px;
  font-weight: 600;
}

.fmip-active {
  background: rgba(220, 53, 69, 0.10);
  color: #dc3545;
}

.fmip-inactive {
  background: rgba(40, 167, 69, 0.12);
  color: #1e7e34;
}

.fmip-unknown {
  background: rgba(108, 117, 125, 0.12);
  color: #6c757d;
}

.fmip-dd {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px 12px;
}

.imei-check-device {
  margin-top: 2px;
  color: #1e7e34;
  font-weight: 600;
}

.fmip-checked {
  color: #6c757d;
  font-size: 13px;
}

.fmip-refresh-error {
  flex-basis: 100%;
}

.ticket-field-block {
  margin-top: 0;
}

.ticket-description {
  margin: 4px 0 0;
  white-space: pre-wrap;
  font-size: 15px;
  line-height: 1.5;
}

.ticket-history {
  margin-top: 24px;
}

.history-title {
  margin: 0 0 16px;
  font-size: 17px;
  font-weight: 600;
}

.history-list {
  list-style: none;
  margin: 0;
  padding: 0;
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

/* Durchgehende Zeitachsen-Schiene hinter allen Einträgen. */
.history-list::before {
  content: '';
  position: absolute;
  left: 4px;
  top: 6px;
  bottom: 6px;
  width: 2px;
  background: var(--border);
}

.history-item {
  position: relative;
  padding-left: 22px;
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 2px 10px;
}

/* Punkt auf der Schiene. */
.history-item::before {
  content: '';
  position: absolute;
  left: 0;
  top: 5px;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: var(--surface);
  border: 2px solid var(--accent);
  box-sizing: border-box;
}

.history-date {
  font-size: 13px;
  font-weight: 600;
  color: #6c757d;
  font-variant-numeric: tabular-nums;
}

.history-user {
  font-size: 13px;
  color: #6c757d;
}

.history-note {
  font-size: 15px;
  line-height: 1.5;
  white-space: pre-wrap;
  flex: 1 1 auto;
}

.history-json {
  flex-basis: 100%;
  margin: 4px 0 0;
  padding: 10px 12px;
  background: #f6f6f7;
  border-radius: 8px;
  font-size: 13px;
  overflow-x: auto;
}

.history-empty {
  color: #6c757d;
  font-size: 14px;
  margin: 0;
}

/* „Weitere N Einträge…" — bündig mit den Einträgen, gleiche Textgröße,
   dezent (kein lautes Orange, kein 16px-Bootstrap-Button). */
.history-more {
  list-style: none;
  padding-left: 22px;
}

.history-more-btn {
  padding: 0;
  border: 0;
  background: none;
  font-size: 14px;
  font-weight: 500;
  line-height: 1.5;
  text-decoration: none;
  color: var(--muted);
  cursor: pointer;
}

.history-more-btn:hover {
  color: var(--accent-hover);
  text-decoration: underline;
}
</style>
